import { isSupportedLanguage, localization, migrateLegacyLocalizationStorage, SupportedLanguage } from "@next-platform/client-common-core";
import appLogger from "@next-platform/client-common-core/logger/appLogger";
import clientStorage from "@next-platform/client-common-core/services/clientStorage";

import { USER_LANGUAGE_PREFERENCE_LOCAL_STORAGE_KEY } from "@/constants/user";
import reportMissingTranslationKeyToTrackJs from "@/infra/reportMissingTranslationKeyToTrackJs";
import { getLocalResources } from "@/utils";

const initializeLocalizationLogger = appLogger.withTag("initializeLocalization");

const extractLanguageFromPath = (): SupportedLanguage | null => {
  const firstSegment = window.location.pathname.split("/")[1];
  return firstSegment && isSupportedLanguage(firstSegment) ? (firstSegment as SupportedLanguage) : null;
};

const extractLanguageFromStorage = async (): Promise<SupportedLanguage | null> => {
  const storedUserLanguagePreference = await clientStorage.getItem(USER_LANGUAGE_PREFERENCE_LOCAL_STORAGE_KEY).catch((readError: unknown) => {
    initializeLocalizationLogger.error("Failed to read the stored language preference", readError);
    return null;
  });
  return storedUserLanguagePreference && isSupportedLanguage(storedUserLanguagePreference)
    ? (storedUserLanguagePreference as SupportedLanguage)
    : null;
};

const extractLanguageFromBrowser = (): SupportedLanguage | null => {
  const browserCode = navigator.language?.split("-")[0];
  return browserCode && isSupportedLanguage(browserCode) ? (browserCode as SupportedLanguage) : null;
};

export const resolvePreferredLanguage = async (): Promise<SupportedLanguage | null> => {
  const languageFromPath = extractLanguageFromPath();
  if (languageFromPath) {
    return languageFromPath;
  }

  const languageFromStorage = await extractLanguageFromStorage();
  if (languageFromStorage) {
    return languageFromStorage;
  }

  return extractLanguageFromBrowser();
};

const IS_LOCALHOST = location.host.indexOf("localhost") === 0;

const initializeLocalization = async ({ appId }: { appId: string }): Promise<void> => {
  try {
    // TODO(hassanahmad): [NX-12172] Remove one-time legacy localization storage migration after end of September 2026.
    await migrateLegacyLocalizationStorage();
    const preferredLanguage = await resolvePreferredLanguage();
    await localization.init({
      appId,
      preferredLanguage,
      getLocalResources,
      reportMissingKey: IS_LOCALHOST ? undefined : reportMissingTranslationKeyToTrackJs,
    });
  } catch (initializationError) {
    initializeLocalizationLogger.error("Failed to initialize localization", initializationError);
  }
};

export default initializeLocalization;
